import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { Router } from '@angular/router';
import * as applicationConstants from '../../../constants/application-constants';
import { environment } from '../../../../environments/environment';
import { pageAnimation } from '@app/core/animations/element.animations';
import { ContactRegistration } from '@app/model/contact.model';
import { Address, NZPostAddress, NZPostDetailsResponse } from '@app/model/address.model';
import { AddressGroupComponent } from '@app/user-on-boarding/components/address-group/address-group.component';
import { AddressSuggestionsService } from '@app/services/address-suggestions.service';
import {
    catchError,
    filter,
    map,
    mapTo,
    pluck,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { merge, Observable, of, Subject } from 'rxjs';
import {
    getInvalidFieldMessage,
    getRequiredFieldMessage,
    getMinCharsMessage,
    getMaxCharsMessage,
    contactNumberRequiredMessage,
    getInvalidCharacterMessage,
    passwordsDoNotMatchMessage
} from '@app/user-on-boarding/shared/constants';
import { Validations } from '@app/constants/validations';
import type { MaskitoOptions } from '@maskito/core';
import mask from '@app/shared/utils/maskito-mask';
import { Store } from '@ngrx/store';
import * as fromApp from '../../../store/app.reducers';
import { LoginAttempt } from '../../../store/auth/auth.actions';

@Component({
    selector: 'app-create-account',
    templateUrl: './create-account.component.html',
    styleUrls: ['./create-account.component.scss'],
    animations: [pageAnimation]
})
export class CreateAccountComponent implements OnInit, OnDestroy {

    @HostBinding('@pageAnimation') animate = true;
    @ViewChild('physicalAddress', { static: false }) physicalAddress: AddressGroupComponent;
    @ViewChild('postalAddress', { static: false }) postalAddress: AddressGroupComponent;

    readonly phoneOptions: MaskitoOptions = mask;

    contactForm: UntypedFormGroup;
    addressForm: UntypedFormGroup;
    readonly = true;
    submitted = false;
    unknownErrorMessages = [];
    isAuthenticating = false;
    showPassword = false;
    showConfirmPassword = false;
    applicationConstants: any;
    successfulUrl: string;
    activeIndex = 0;
    sameAsAddressDisabledMessage = 'The above Delivery address is not available for Standard Mail delivery';

    physicalAddressInput$ = new Subject<NZPostAddress>();
    postalAddress$ = new Subject<Partial<NZPostAddress>>();
    setAddressesSameInput$ = new Subject<boolean>();
    postalAddressFromPhysicalAddress$: Observable<NZPostAddress>;

    physicalAddress$: Observable<Partial<NZPostAddress>>;
    setAddressesSame$: Observable<boolean>;
    isSameAddressEnabled$: Observable<boolean>;

    private unsubscribe$ = new Subject<void>();

    public readonly firstNameMaxCharacters = Validations.FIRST_NAME_MAX_CHARACTERS;
    public readonly lastNameMaxCharacters = Validations.LAST_NAME_MAX_CHARACTERS;

    constructor(
        private fb: UntypedFormBuilder,
        private auth: AuthService,
        public addressService: AddressSuggestionsService,
        private router: Router,
        private store: Store<fromApp.AppState>) {
    }

    ngOnInit() {
        this.createForms();
        this.applicationConstants = applicationConstants.ApplicationConstants;
        this.successfulUrl = !environment.checkAccountVerification ? '/checkout/steps/user-details' : '/';
        this.initAddressesBehaviour();

        this.contactForm.get('password').valueChanges.subscribe(() => {
            this.contactForm.get('confirmPassword').updateValueAndValidity({ emitEvent: false });
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createForms() {
        this.contactForm = this.fb.group({ // <-- the parent FormGroup
            firstName: ['', Validations.FIRST_NAME],
            lastName: ['', Validations.LAST_NAME],
            email: ['', Validations.EMAIL],
            company: ['', Validations.COMPANY],
            mobilePhone: ['', Validations.MOBILE_PHONE],
            homePhone: ['', Validations.HOME_PHONE],
            homePhoneExt: ['', Validations.PHONE_EXTENSION],
            password: ['', Validations.PASSWORD],
            confirmPassword: ['', Validations.CONFIRM_PASSWORD('password')]
        });

        const defaultAddress = new Address();

        this.addressForm = this.fb.group({
            physicalAddress: this.fb.group({
                ...defaultAddress,
                line1: ['', Validations.ADDRESS_LINE_1],
                line2: ['', Validations.ADDRESS_LINE_2],
                townOrCity: ['', Validations.ADDRESS_TOWN_OR_CITY],
                postCode: ['', Validations.ADDRESS_POST_CODE],
                province: ['', Validations.ADDRESS_PROVINCE],
                country: new UntypedFormControl({ value: 'New Zealand', disabled: true }, Validations.ADDRESS_COUNTRY),
                nzPostDpId: ['', Validations.ADDRESS_NZPOST_DPID]
            }),
            postalAddress: this.fb.group({
                ...defaultAddress,
                line1: ['', Validations.ADDRESS_LINE_1],
                line2: ['', Validations.ADDRESS_LINE_2],
                townOrCity: ['', Validations.ADDRESS_TOWN_OR_CITY],
                postCode: ['', Validations.ADDRESS_POST_CODE],
                province: ['', Validations.ADDRESS_PROVINCE],
                country: new UntypedFormControl({ value: 'New Zealand', disabled: true }, Validations.ADDRESS_COUNTRY),
                nzPostDpId: ['', Validations.ADDRESS_NZPOST_DPID]
            }),
            portalTcAccepted: [false, Validations.TERMS_AND_CONDITIONS],
        });
    }

    onLogin() {
        this.router.navigate(['account', 'login']);
    }

    onContinueToAddressTab() {
    // update the validation of all contact controls
        this.contactForm.updateValueAndValidity();
        this.markFormGroupDirtyTouched(this.contactForm);
        if (!this.contactForm.valid) {
            return;
        }
        this.clearErrors();
        this.contactForm.updateValueAndValidity();
        // switch to Address tab
        this.activeIndex = 1;
    }

    onCreateAccount() {
        this.submitted = true;

        // update the validation of all address controls
        this.addressForm.updateValueAndValidity();
        this.markFormGroupDirtyTouched(this.addressForm);

        if (!this.addressForm.valid) {
            return;
        }

        this.clearErrors();
        this.addressForm.updateValueAndValidity();
        if (!environment.checkAccountVerification || !localStorage.getItem('auth-entry-url')) {
            localStorage.setItem('auth-entry-url', this.successfulUrl);
        }
        localStorage.setItem('login-entry', 'create-user');
        this.isAuthenticating = true;

        // send this request to the contact api
        const contactRegistration = new ContactRegistration(
            this.contactForm.get('firstName').value,
            this.contactForm.get('lastName').value,
            this.contactForm.get('email').value,
            this.contactForm.get('company').value,
            this.getNationFormattedPhoneNumber(this.contactForm.get('mobilePhone').value),
            this.constructHomePhone(this.contactForm),
            this.contactForm.get('password').value,
            this.getAddressFromFormGroup(<UntypedFormGroup> this.addressForm.get('physicalAddress')),
            this.getAddressFromFormGroup(<UntypedFormGroup> this.addressForm.get('postalAddress')),
            this.addressForm.get('portalTcAccepted').value
        );
        // Use the new MyLab API registration endpoint
        this.auth.registerWithMyLabAPI(contactRegistration).subscribe({
            next: (response) => {
                this.isAuthenticating = false;
                localStorage.removeItem('auth-entry-url');
                localStorage.removeItem('login-entry');

                if (environment.useUniversalLogin) {
                    return this.router.navigate(['account', 'registration-successful']);
                }

                this.store.dispatch(new LoginAttempt(
                    contactRegistration.email,
                    contactRegistration.password,
                    false
                ));
            },
            error: (error) => {
                this.isAuthenticating = false;
                localStorage.removeItem('auth-entry-url');
                localStorage.removeItem('login-entry');

                if (error.status === 400 && error.result?.errors) {
                    // Handle validation errors
                    Object.keys(error.result.errors).forEach(key => {
                        error.result.errors[key].forEach(errorMsg => {
                            this.unknownErrorMessages.push(errorMsg);
                        });
                    });
                } else if (error.message) {
                    this.unknownErrorMessages.push(error.message);
                } else {
                    const msg = 'Registration failed. Please try again.';
                    this.unknownErrorMessages.push(msg);
                }

                // Navigate back to contact form
                this.activeIndex = 0;
            }
        });
    }

    getAddressFromFormGroup(formGroup: UntypedFormGroup) {
        const address = new Address(
            formGroup.get('recipientInfo').value,
            formGroup.get('line1').value,
            formGroup.get('line2').value,
            formGroup.get('townOrCity').value,
            formGroup.get('province').value,
            formGroup.get('postCode').value,
            formGroup.get('country').value,
        );
        address.nzPostDpId = formGroup.get('nzPostDpId').value;
        return address;
    }


    getErrorMessage(formControlName: string) {
        const formControl = this.contactForm.get(formControlName);
        if (formControl.hasError('missingContactNumber')) {
            return contactNumberRequiredMessage;
        } else if (formControl.hasError('doesNotMatch')) {
            return passwordsDoNotMatchMessage;
        } else if (formControl.hasError('required')) {
            return getRequiredFieldMessage(formControlName);
        } else if (formControl.hasError('minlength')) {
            return getMinCharsMessage(formControlName, formControl.errors.minlength.requiredLength);
        } else if (formControl.hasError('maxlength')) {
            return getMaxCharsMessage(formControlName, formControl.errors.maxlength.requiredLength);
        } else if (formControl.hasError('invalidCharacter')) {
            return getInvalidCharacterMessage(formControlName);
        } else if (formControl.hasError('pattern') || formControl.hasError(formControlName)) {
            return getInvalidFieldMessage(formControlName);
        }
    }

    clearErrors() {
        this.unknownErrorMessages = [];
        this.clearControlErrors(this.contactForm);
        this.clearControlErrors(this.addressForm);
    }

    getMessage(rule) {
        let theString = rule['message'];
        const format = !!rule['format'] ? rule['format'] : [];

        if (!!rule.format) {
            for (let i = 0; i < rule['format'].length; i++) {
                // "gm" = RegEx options for Global search (more than one instance)
                // and for Multiline search
                const regEx = new RegExp('%d');
                theString = theString.replace(regEx, format[i]);

            }
        }

        return theString;
    }

    onFormStepClicked(index: number) {
        if (index === 1) {
            this.onContinueToAddressTab();
        } else {
            this.activeIndex = index;
        }
    }

    isNzPostAddressPhysical(nzPostAddress: NZPostAddress) {
        return !!nzPostAddress && nzPostAddress.SourceDesc && nzPostAddress.SourceDesc.indexOf('Physical') >= 0;
    }

    isNzPostAddressPostal(nzPostAddress: NZPostAddress) {
        return !!nzPostAddress && nzPostAddress.SourceDesc && nzPostAddress.SourceDesc.indexOf('Postal') >= 0;
    }

    onPhysicalAddressSelected(nzPostAddress: NZPostAddress) {
        this.physicalAddressInput$.next(nzPostAddress);
    }

    onPostalAddressSelected(nzPostAddress: NZPostAddress) {
        this.postalAddress$.next(nzPostAddress !== null ? nzPostAddress : {});
    }

    onIsPostalAddressSameAsPhysicalAddressChanged(value) {
        this.setAddressesSameInput$.next(value);
    }

    /**
   * Marks all controls in a form group as touched
   * @param formGroup - The form group to touch
   */
    private markFormGroupDirtyTouched(formGroup: UntypedFormGroup) {
        for (const key of Object.keys(formGroup.controls)) {
            if (formGroup.controls[key] instanceof UntypedFormGroup) {
                this.markFormGroupDirtyTouched.bind(this, formGroup.controls[key]);
            }
            formGroup.controls[key].markAsDirty();
            formGroup.controls[key].updateValueAndValidity();
        }
        formGroup.markAllAsTouched();
    }

    private clearControlErrors(formGroup: UntypedFormGroup) {
        for (const key of Object.keys(formGroup.controls)) {
            if (formGroup.controls[key] instanceof UntypedFormGroup) {
                this.clearControlErrors.bind(this, formGroup.controls[key]);
            }
            formGroup.controls[key].setErrors(null);
        }
    }

    private initAddressesBehaviour() {

        this.physicalAddress$ = this.physicalAddressInput$.pipe(
            filter(Boolean),
            shareReplay(1)
        );

        this.postalAddressFromPhysicalAddress$ = this.physicalAddress$.pipe(
            switchMap((physicalAddress: NZPostAddress) => {
                const isPhysicalAlsoPostal = this.isNzPostAddressPostal(physicalAddress);
                if (isPhysicalAlsoPostal) {
                    return of(physicalAddress);
                }

                return this.getPostalAddressWithDPID$(physicalAddress.DPID).pipe(
                    map((address: Partial<NZPostAddress>) => <NZPostAddress>address)
                );
            }),
            shareReplay(1)
        );

        this.setAddressesSame$ = merge(
            this.setAddressesSameInput$,
            this.postalAddressFromPhysicalAddress$.pipe(
                filter(address => address === null),
                mapTo(false)
            )
        ).pipe(
            startWith(false),
            shareReplay(1)
        );

        this.isSameAddressEnabled$ = this.postalAddressFromPhysicalAddress$.pipe(
            map(address => address !== null),
            startWith(true),
            shareReplay(1)
        );

        const postalAddressOnPhysicalAddressChange$ = this.getPostalHandler$();
        const postalAddressOnSetAddresesTheSame$ = this.getAddressTheSameHandler$();

        // set postal address on delivery address change and
        // on set postal address same with delivery address
        merge(
            postalAddressOnPhysicalAddressChange$,
            postalAddressOnSetAddresesTheSame$
        ).pipe(
            tap(address => {
                if (this.postalAddress) {
                    this.postalAddress.setSelectedNzPostAddress(address);
                }
            }),
            tap(address => this.postalAddress$.next(address)),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    private getPostalHandler$() {
        return this.postalAddressFromPhysicalAddress$.pipe(
            withLatestFrom(this.setAddressesSameInput$),
            filter(([, areAddressesTheSame]) => areAddressesTheSame),
            map(([address]) => address !== null ? address : {})
        );
    }

    private getAddressTheSameHandler$() {
        return this.setAddressesSameInput$.pipe(
            startWith(true),
            filter(Boolean),
            withLatestFrom(this.postalAddressFromPhysicalAddress$),
            pluck('1'), // get the second entry
            map((address): Partial<NZPostAddress> => address),
        );
    }

    private getPostalAddressWithDPID$(dpid: string): Observable<Partial<NZPostAddress>> {

        if (!dpid) {
            return of(null);
        }

        return this.addressService.getDetail$(dpid, 'postal').pipe(
            map((result: NZPostDetailsResponse) => {
                if (!!result.success) {
                    const [detail] = result.details;
                    if (!!detail && detail.SourceDesc.includes('Postal')) {
                        return detail;
                    }
                }
                return null;
            }),
            catchError(e => [null]),
            take(1)
        );
    }

    private constructHomePhone(formGroup: UntypedFormGroup) {
        const phoneNumber = this.getNationFormattedPhoneNumber(formGroup.get('homePhone').value);
        const extension = formGroup.get('homePhoneExt').value;
        if (extension){
            return `${phoneNumber} ext ${extension}`;
        } else {
            return phoneNumber;
        }
    }

    private getNationFormattedPhoneNumber(input: string) {
        let output = input;
        output = output.replace(/^\+64 ?/, '0'); // Replace '+64 ' with 0
        output = output.replace(/^00/, '0'); // After last replace we might have 0027..., fix that
        return output;
    }
}
